/**
 * Battleship Cartridge Implementation
 */
import { BattleshipEngine, GRID_SIZE, SHIPS } from './engine.js';
import { BattleshipAI } from './ai.js';

export const Cartridge = {
  id: 'battleship',
  name: 'Battleship',
  tagline: 'Naval strategy & tactical artillery duel',
  icon: '⚓🚢',
  category: 'Strategy',
  minPlayers: 1,
  maxPlayers: 2,
  modes: ['solo', 'multiplayer'],

  defaultConfig: {},
  configOptions: [],

  async init(mountElement, context) {
    this.mountElement = mountElement;
    this.context = context;
    this.engine = new BattleshipEngine();
    this.ai = new BattleshipAI();

    this.phase = 'placement'; // 'placement' | 'battle' | 'gameover'
    this.currentView = 'radar'; // 'radar' (opponent) | 'fleet' (own)
    this.selectedShipIdx = 0;
    this.placeHorizontal = true;
    this.opponentReady = false;
    this.isMyTurn = context.isHost !== false; // Host fires first

    // Auto-place player's initial fleet for convenience
    this.engine.generateRandomFleet();

    this.buildDOM();
    this.render();
  },

  buildDOM() {
    this.mountElement.innerHTML = `
      <div class="battleship-wrapper" style="display:flex; flex-direction:column; align-items:center; width:100%; max-width:540px; margin:0 auto;">
        <div class="game-hud">
          <div class="hud-player">
            <span>⚓ You</span>
          </div>
          <div id="bs-turn-badge" class="hud-turn-indicator active">Deploy Fleet</div>
          <div class="hud-player">
            <span>🎯 ${this.context.mode === 'solo' ? 'Admiral AI' : this.context.opponentName || 'Opponent'}</span>
          </div>
        </div>

        <!-- Mode / View Switcher (Battle Phase) -->
        <div id="bs-view-switcher" style="display:none; width:100%; margin-bottom:12px; gap:8px;">
          <button id="bs-tab-radar" class="btn btn-primary btn-sm" style="flex:1;">🎯 Attack Radar</button>
          <button id="bs-tab-fleet" class="btn btn-secondary btn-sm" style="flex:1;">🛡️ Your Fleet</button>
        </div>

        <!-- Fleet Status Panel -->
        <div id="bs-fleet-status" style="display:flex; gap:6px; margin-bottom:10px; font-size:0.75rem; flex-wrap:wrap; justify-content:center;">
          ${SHIPS.map((s) => `
            <span id="bs-ship-pill-${s.id}" style="background:var(--bg-surface); padding:4px 8px; border-radius:6px; border:1px solid var(--border-subtle);">
              ${s.icon} ${s.name} (${s.size})
            </span>
          `).join('')}
        </div>

        <!-- 10x10 Grid Container -->
        <div id="bs-grid-container" style="display:grid; grid-template-columns:repeat(10, 1fr); gap:2px; width:100%; aspect-ratio:1/1; max-width:440px; background:#1e293b; padding:4px; border-radius:12px; border:2px solid #334155; touch-action:manipulation;">
          <!-- 100 Cells dynamically rendered -->
        </div>

        <!-- Controls Container -->
        <div id="bs-controls" style="margin-top:14px; width:100%; display:flex; flex-direction:column; gap:10px;">
          <div id="bs-placement-bar" style="display:flex; gap:8px; width:100%;">
            <button id="bs-rotate-btn" class="btn btn-secondary" style="flex:1;">🔄 Rotate (${this.placeHorizontal ? 'Horiz' : 'Vert'})</button>
            <button id="bs-random-btn" class="btn btn-secondary" style="flex:1;">🎲 Auto Deploy</button>
            <button id="bs-ready-btn" class="btn btn-primary" style="flex:1.2;">⚔️ Start Battle</button>
          </div>
        </div>
      </div>
    `;

    this.turnBadge = this.mountElement.querySelector('#bs-turn-badge');
    this.viewSwitcher = this.mountElement.querySelector('#bs-view-switcher');
    this.tabRadar = this.mountElement.querySelector('#bs-tab-radar');
    this.tabFleet = this.mountElement.querySelector('#bs-tab-fleet');
    this.gridContainer = this.mountElement.querySelector('#bs-grid-container');
    this.placementBar = this.mountElement.querySelector('#bs-placement-bar');

    this.tabRadar.addEventListener('click', () => {
      this.currentView = 'radar';
      this.tabRadar.className = 'btn btn-primary btn-sm';
      this.tabFleet.className = 'btn btn-secondary btn-sm';
      this.render();
    });

    this.tabFleet.addEventListener('click', () => {
      this.currentView = 'fleet';
      this.tabFleet.className = 'btn btn-primary btn-sm';
      this.tabRadar.className = 'btn btn-secondary btn-sm';
      this.render();
    });

    this.mountElement.querySelector('#bs-rotate-btn').addEventListener('click', (e) => {
      this.placeHorizontal = !this.placeHorizontal;
      e.target.textContent = `🔄 Rotate (${this.placeHorizontal ? 'Horiz' : 'Vert'})`;
      this.context.audio?.playSfx('tap');
    });

    this.mountElement.querySelector('#bs-random-btn').addEventListener('click', () => {
      this.engine.generateRandomFleet();
      this.context.audio?.playSfx('tap');
      this.render();
    });

    this.mountElement.querySelector('#bs-ready-btn').addEventListener('click', () => {
      this.handlePlayerReady();
    });
  },

  handlePlayerReady() {
    this.context.audio?.playSfx('success');
    this.phase = 'battle';
    this.placementBar.style.display = 'none';
    this.viewSwitcher.style.display = 'flex';

    if (this.context.mode === 'solo') {
      this.opponentReady = true;
      this.updateHUD();
      this.render();
    } else {
      // In multiplayer, notify peer we are ready
      this.turnBadge.textContent = 'Waiting for opponent...';
      this.context.sendAction('ready', { fleetCount: this.engine.ships.length });
    }
  },

  handleCellClick(x, y) {
    if (this.phase === 'placement') {
      // Place selected ship
      const def = SHIPS[this.selectedShipIdx];
      if (def) {
        const placed = this.engine.placeShip(def, x, y, this.placeHorizontal);
        if (placed) {
          this.context.audio?.playSfx('place');
          this.selectedShipIdx = (this.selectedShipIdx + 1) % SHIPS.length;
          this.render();
        } else {
          this.context.audio?.playSfx('error');
        }
      }
      return;
    }

    if (this.phase === 'battle') {
      if (this.currentView !== 'radar') return; // Can only fire on Radar view
      if (!this.isMyTurn) {
        this.context.audio?.playSfx('error');
        return;
      }

      if (this.engine.hasFiredAt(x, y)) {
        this.context.audio?.playSfx('error');
        return;
      }

      this.fireShot(x, y);
    }
  },

  fireShot(x, y) {
    if (this.context.mode === 'solo') {
      // Execute shot against AI
      const result = this.ai.engine.receiveShot(x, y);
      this.engine.recordFiredShot(x, y, result.hit, result.sunk, result.allSunk);

      if (result.hit) {
        this.context.audio?.playSfx(result.sunk ? 'sunk' : 'hit');
      } else {
        this.context.audio?.playSfx('miss');
      }

      this.render();

      if (result.allSunk) {
        this.handleGameOver(true);
        return;
      }

      // AI's turn
      this.isMyTurn = false;
      this.updateHUD();

      setTimeout(() => {
        if (this.phase !== 'battle') return;
        const aiShot = this.ai.getNextShot();
        const playerResult = this.engine.receiveShot(aiShot.x, aiShot.y);
        this.ai.recordShotResult(aiShot.x, aiShot.y, playerResult.hit, playerResult.sunk);

        if (playerResult.hit) {
          this.context.audio?.playSfx(playerResult.sunk ? 'sunk' : 'hit');
        }

        this.isMyTurn = true;
        this.render();

        if (playerResult.allSunk) {
          this.handleGameOver(false);
        }
      }, 700);

    } else {
      // Multiplayer shot
      this.isMyTurn = false;
      this.updateHUD();
      this.context.sendAction('fire', { x, y });
    }
  },

  handleRemoteAction(action) {
    if (action.actionType === 'ready') {
      this.opponentReady = true;
      this.updateHUD();
      this.render();
    } else if (action.actionType === 'fire') {
      // Incoming enemy shot
      const { x, y } = action.payload;
      const result = this.engine.receiveShot(x, y);

      if (result.hit) {
        this.context.audio?.playSfx(result.sunk ? 'sunk' : 'hit');
      } else {
        this.context.audio?.playSfx('miss');
      }

      // Reply with shot result
      this.context.sendAction('shot_result', {
        x,
        y,
        hit: result.hit,
        sunk: result.sunk,
        allSunk: result.allSunk
      });

      this.isMyTurn = true;
      this.render();

      if (result.allSunk) {
        this.handleGameOver(false); // Opponent sunk all my ships
      }
    } else if (action.actionType === 'shot_result') {
      // Opponent evaluated our shot
      const { x, y, hit, sunk, allSunk } = action.payload;
      this.engine.recordFiredShot(x, y, hit, sunk, allSunk);

      if (hit) {
        this.context.audio?.playSfx(sunk ? 'sunk' : 'hit');
      } else {
        this.context.audio?.playSfx('miss');
      }

      this.render();

      if (allSunk) {
        this.handleGameOver(true); // We sunk all opponent ships!
      }
    }
  },

  handleGameOver(isWin) {
    this.phase = 'gameover';
    this.context.audio?.playSfx(isWin ? 'victory' : 'defeat');
    this.context.onGameOver?.({
      winnerId: isWin ? this.context.playerId : this.context.opponentId,
      isDraw: false,
      reason: isWin ? 'All enemy naval vessels eliminated!' : 'Your fleet was completely sunk!',
      stats: { shots: this.engine.shotsFired.size }
    });
    this.updateHUD();
  },

  updateHUD() {
    if (!this.turnBadge) return;

    if (this.phase === 'placement') {
      this.turnBadge.textContent = 'Deploy Fleet';
      this.turnBadge.className = 'hud-turn-indicator active';
    } else if (this.phase === 'gameover') {
      this.turnBadge.textContent = 'Battle Concluded';
      this.turnBadge.className = 'hud-turn-indicator waiting';
    } else {
      if (this.isMyTurn) {
        this.turnBadge.textContent = 'Fire Cannon!';
        this.turnBadge.className = 'hud-turn-indicator active';
      } else {
        this.turnBadge.textContent = 'Enemy Firing...';
        this.turnBadge.className = 'hud-turn-indicator waiting';
      }
    }
  },

  render() {
    this.updateHUD();
    this.gridContainer.innerHTML = '';

    const isRadar = this.currentView === 'radar' && this.phase === 'battle';

    for (let y = 0; y < GRID_SIZE; y++) {
      for (let x = 0; x < GRID_SIZE; x++) {
        const cell = document.createElement('div');
        cell.className = 'bs-cell';
        cell.style.cssText = `
          aspect-ratio: 1/1;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.1rem;
          cursor: pointer;
          transition: background 0.15s ease;
          background: #0f172a;
          border: 1px solid #1e293b;
        `;

        if (isRadar) {
          // Radar view (shots fired at opponent)
          const key = `${x},${y}`;
          const shot = this.engine.shotsFired.get(key);

          if (shot) {
            if (shot.hit) {
              cell.style.background = '#e11d48';
              cell.textContent = '💥';
            } else {
              cell.style.background = '#38bdf8';
              cell.textContent = '💦';
            }
          } else {
            cell.addEventListener('mouseenter', () => (cell.style.background = '#334155'));
            cell.addEventListener('mouseleave', () => (cell.style.background = '#0f172a'));
          }
        } else {
          // Fleet view (own placed ships & incoming enemy hits)
          let hasShip = false;
          for (const s of this.engine.ships) {
            if (s.coords.some((c) => c.x === x && c.y === y)) {
              hasShip = true;
              break;
            }
          }

          const key = `${x},${y}`;
          const shot = this.engine.shotsReceived.get(key);

          if (shot) {
            if (shot.hit) {
              cell.style.background = '#e11d48';
              cell.textContent = '🔥';
            } else {
              cell.style.background = '#1e293b';
              cell.textContent = '🌊';
            }
          } else if (hasShip) {
            cell.style.background = '#0284c7';
            cell.textContent = '🚢';
          }
        }

        cell.addEventListener('click', () => this.handleCellClick(x, y));
        this.gridContainer.appendChild(cell);
      }
    }
  },

  serializeState() {
    return {
      phase: this.phase,
      fleetCount: this.engine.ships.length,
      shotsFired: Array.from(this.engine.shotsFired.entries()),
      shotsReceived: Array.from(this.engine.shotsReceived.entries())
    };
  },

  restoreState(state) {
    if (state) {
      this.phase = state.phase || 'placement';
      this.render();
    }
  },

  pause() {},
  resume() {},
  destroy() {}
};
